import { processRequest } from '../../services/Api';
import { put, call, takeLatest, all } from 'redux-saga/effects';
import { stopSubmit } from 'redux-form';
import {
	filterKeysStartingWithUtm,
	getCookie,
	getLocalStorageItem,
	getMarketingTags,
	removeCookie,
	removeUtmTags,
	setGTMData,
} from '../../services/Helper';
import { COOKIES } from '../../services/Constants';
import { IS_MOBILE_APP } from '../../services/capacitor/Constants';
import { historyPush, historyReplace } from '../../services/History';
import { setDeviceTrustToken, signInActionTypes } from './Constants';
import { removeActivationCodeCookie, removeLicenseCodeCookie } from '../activation/Constants';
import * as signInActions from './Actions';
import * as signUpActions from '../signUp/Actions';
import * as pushNotificationActions from '../../components/appPushNotification/Actions';
import * as errorHandlerActions from '../../components/errorHandler/Actions';

export default function* () {
	yield all([
		takeLatest(signInActionTypes.LOGIN_REQUEST, handleLoginRequest),
		takeLatest(signInActionTypes.VERIFY_TFA_CODE_REQUEST, handleVerifyTFACodeRequest),
		takeLatest(signInActionTypes.FIRST_VERIFY_REQUEST, handleFirstSocialVerifyRequest),
		takeLatest(signInActionTypes.HANDLE_STEPS_AFTER_LOGIN, handleStepsAfterLogin),
	]);
}

export function* handleStepsAfterLogin({ data, path }) {
	const deviceToken = getLocalStorageItem('deviceToken');
	const activationCode = getCookie(COOKIES.activationCode);
	const license = getCookie(COOKIES.license);

	const { auth_token, activation_code, license: licenseResponse, trusted_device } = data || {};
	const { token, expires_at } = trusted_device || {};
	const { success: activationCodeSuccess } = activation_code || {};
	const { success: licenseSuccess } = licenseResponse || {};

	yield put(signInActions.loginSuccess(auth_token));

	if (token) setDeviceTrustToken(token, expires_at);

	if (license) {
		if (licenseSuccess) {
			yield call(historyReplace, '/license-applying');
		} else {
			yield call(historyReplace, `/licenses/${license}/apply`);
			yield call(removeLicenseCodeCookie);
		}
	} else if (activationCode) {
		if (activationCodeSuccess) {
			yield call(historyReplace, '/activation-code-applying');
		} else {
			yield call(historyReplace, `/activeren/${activationCode}/apply`);
			yield call(removeActivationCodeCookie);
		}
	} else {
		yield call(historyReplace, path || '/');
	}

	if ((IS_MOBILE_APP && deviceToken) || deviceToken === 'shouldRestore') {
		yield put(pushNotificationActions.registerPushNotificationForDeviceRequest(deviceToken));
	}
}

export function* handleLoginRequest({ payload }) {
	try {
		const { email, password, path, recaptchaToken } = payload || {};
		const activationCode = getCookie(COOKIES.activationCode);
		const license = getCookie(COOKIES.license);
		const deviceTrustToken = getCookie(COOKIES.deviceTrustToken);
		const requestBody = { email, password };

		if (activationCode && !license) {
			requestBody.activation_code = activationCode;
		}
		if (license) {
			requestBody.license_secret = license;
		}
		if (recaptchaToken) {
			requestBody.recaptcha_token = recaptchaToken;
		}

		const { data } = yield call(
			processRequest,
			'auth/get_token',
			'POST',
			requestBody,
			false,
			false,
			deviceTrustToken ? { 'X-Mfa-Trusted-Device-Token': deviceTrustToken } : undefined,
		);

		const { token, mfa_required, mfa_setup_required } = data || {};

		if (mfa_required || mfa_setup_required) {
			yield put(signInActions.executeTFA({ mfa_required, mfa_setup_required, token }));
			yield put(signInActions.loginError());
			return;
		}

		yield call(handleStepsAfterLogin, { data, path });
	} catch (e) {
		const { status, data } = e.response || {};
		const { info } = data || {};

		if (status === 429) {
			yield put(signInActions.executeCaptcha());
		} else {
			yield put(stopSubmit('signInForm', { password: info }));
			yield put(signInActions.loginError());
		}
	}
}

export function* handleFirstSocialVerifyRequest({ payload }) {
	try {
		const {
			accessToken,
			tokenId,
			code,
			id_token,
			signedRequest,
			provider,
			user,
			is_limited_login,
			authenticationToken,
		} = payload || {};
		const marketingTags = getMarketingTags();
		const lastPageSlug = getCookie(COOKIES.lastPageSlug);
		const utmTags = marketingTags && filterKeysStartingWithUtm(marketingTags);
		const activationCode = getCookie(COOKIES.activationCode);
		const license = getCookie(COOKIES.license);
		const deviceToken = getLocalStorageItem('deviceToken');
		const authentication = {
			social_authentication: {
				code,
				id_token,
				provider,
				signed_request: signedRequest,
				token_id: tokenId,
				...(accessToken ? { access_token: accessToken } : {}),
				...(authenticationToken ? { jwt_token: authenticationToken } : {}),
				...(is_limited_login ? { is_limited_login } : {}),
			},
		};

		if (activationCode && !license) {
			authentication.social_authentication.activation_code = activationCode;
		}

		if (license) {
			authentication.social_authentication.license_secret = license;
		}

		if (utmTags) {
			authentication.social_authentication.utm_tags = utmTags;
		}

		if (lastPageSlug) {
			authentication.social_authentication.last_page_slug = lastPageSlug;
		}

		const {
			data: { social_authentication },
		} = yield call(processRequest, 'public/social_authentications', 'POST', authentication);

		if (!social_authentication.newly_created_user) {
			yield put(signInActions.loginSuccess(social_authentication.auth_token));

			if ((IS_MOBILE_APP && deviceToken) || deviceToken === 'shouldRestore') {
				yield put(
					pushNotificationActions.registerPushNotificationForDeviceRequest(deviceToken),
				);
			}
			yield call(removeUtmTags, utmTags);
			yield call(removeCookie, COOKIES.lastPageSlug);
			return yield call(historyReplace, '/');
		}

		setGTMData({ event: 'registrationStudent' });

		const initialValues = { ...social_authentication.user_account };
		if (user) {
			initialValues.first_name = user.name.firstName;
			initialValues.last_name = user.name.lastName;
		}
		yield put(
			signUpActions.setInitialValues({
				initialValues,
			}),
		);
		yield put(signInActions.setSocialLoginState({ socialLoading: false }));
		yield put(
			signInActions.firstSocialVerifySuccess({
				token: social_authentication.auth_token,
				userId: social_authentication.user_account.user_id,
			}),
		);
		yield put(signUpActions.setSocialLogin({ isSocialLogin: true }));
		yield call(historyPush, '/sign-up');
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'notification'));
		yield put(signInActions.firstSocialVerifyError());
	}
}

export function* handleVerifyTFACodeRequest({ payload }) {
	const { code: totp_code, trustDevice: trust_device, path, errorCallback } = payload || {};

	try {
		const { data } = yield call(
			processRequest,
			'auth/verify_mfa',
			'POST',
			{
				totp_code,
				trust_device,
			},
			false,
			false,
			{ 'X-MFA-Token': getCookie(COOKIES.mfaAccessToken) || '' },
		);

		yield call(handleStepsAfterLogin, { data, path });

		yield put(signInActions.verifyTFACodeSuccess());
	} catch (e) {
		const { response } = e || {};
		const { status } = response || {};

		if (status === 401) {
			if (errorCallback) errorCallback(response);
		} else {
			yield put(errorHandlerActions.handleError(e, 'notification'));
		}

		yield put(signInActions.verifyTFACodeError());
	}
}
